import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { useDragStore } from "../stores/dragStore";
import { setSceneDragActive } from "./productDragScope";

/**
 * Marks this canvas as the one a part drag is happening in, and takes r3f's
 * pointer events off the page for the length of the gesture.
 *
 * Both follow from the same fact, which is why they live together. Mounted only
 * by the editor's viewer: the preview and thumbnail canvases mount the stage
 * without it, so they never mark themselves and keep drawing the settled image
 * (see `productDragScope`).
 *
 * ## Why the events go away
 *
 * r3f raycasts the interactive objects of the scene on every `pointermove`, and
 * every part carries pointer handlers (see `stopPartEventPropagation` in
 * `ProductViewerPartTree`), so each move walks part geometry through
 * `checkGeometryIntersection`, reading vertex positions one component at a time.
 * A drag needs none of it: `DraggablePart` switches to native `pointermove` and
 * `pointerup` listeners on `globalThis` for the length of the gesture
 * (`attachDragPointerListeners`) and resolves the pointer against a mathematical
 * `Plane`, which is one ray/plane intersection instead of a scene traversal.
 *
 * Measured on a production build with pointer input paced at 125 Hz, a four
 * second resize drag spent about 360 ms of main-thread time in that raycast
 * (`_computeIntersections`, `checkGeometryIntersection`, `getVertexPosition`,
 * `getX`/`getY`/`getZ` and r3f's `handleIntersects`) — the largest single
 * cluster in the gesture. Removing it took main-thread work from 8.9 to
 * 5.1 ms/frame and the worst blocking task from about 300 ms to 50 ms.
 *
 * ## Why `enabled`, not `disconnect`
 *
 * `disconnect()` would also skip the raycast, and it has one advantage: the
 * hover r3f recorded at pointer-down survives the gesture, where `enabled`
 * lets the first suppressed `pointermove` reach `cancelPointer([])` and deliver
 * `pointerout` to everything hovered, so the part being dragged stops looking
 * hovered while it is dragged.
 *
 * It is not worth it. `disconnect()` writes `connected: undefined` into r3f's
 * store, and `ProductViewerCamera`'s `useControlsElement` selects exactly that
 * value, so the element its pointer effect is keyed on changes at every drag
 * start and again at every drag end. That effect owns `activePointers`,
 * `lastWheelPick` and `wheelPivot` as locals, so they were being recreated
 * empty mid-gesture: a second finger put down during a drag read
 * `otherActivePointers` as 0, and a two-finger pinch was taken for a
 * single-finger press that re-seats the orbit pivot.
 *
 * `setEvents({ enabled })` leaves `connected` alone, so that selector's value
 * does not change and the camera is untouched. A latched hover highlight for
 * the length of one gesture is the price, and it is cosmetic.
 */
export function ProductViewerDragScope() {
  const get = useThree((state) => state.get);
  const scene = useThree((state) => state.scene);

  useEffect(() => {
    // What to restore, or null when this component is not suppressing. A host
    // may have mounted the viewer with events already off, and a drag must not
    // turn them on.
    let restoreTo: boolean | null = null;

    const suppress = (gestureInFlight: boolean) => {
      const { events, setEvents } = get();
      if (gestureInFlight) {
        if (restoreTo !== null) return;
        restoreTo = events.enabled;
        if (restoreTo) setEvents({ enabled: false });
        return;
      }
      if (restoreTo === null) return;
      const previous = restoreTo;
      restoreTo = null;
      if (previous) setEvents({ enabled: true });
    };

    // Two signals, deliberately: the listeners come back the moment the
    // pointer is up (`isGestureInFlight`), because holding them off over
    // `endDrag`'s 100 ms click-suppression tail swallows the press that starts
    // the next drag. The scene stays marked for that tail (`isDragActive`), so
    // the shadow, the floor and post-processing settle before they go back to
    // full quality.
    suppress(useDragStore.getState().isGestureInFlight);
    setSceneDragActive(scene, useDragStore.getState().isDragActive);
    const unsubscribe = useDragStore.subscribe((state, previous) => {
      if (state.isGestureInFlight !== previous.isGestureInFlight) {
        suppress(state.isGestureInFlight);
      }
      if (state.isDragActive !== previous.isDragActive) {
        setSceneDragActive(scene, state.isDragActive);
      }
    });

    // Safety net. `DraggablePart` ends its session on `pointerup` and
    // `pointercancel` only, so a gesture that loses its terminal event — the
    // window is switched mid-drag and the button released elsewhere — would
    // otherwise leave this canvas deaf to pointer events for the rest of the
    // session. Losing focus can only mean the gesture is no longer ours.
    // Ends the session rather than only restoring input: leaving
    // `isGestureInFlight` and `isDragActive` set keeps this scene on its
    // interacting shadow and floor for the rest of the session, which is the
    // half of the problem that re-enabling input alone does not solve. The
    // store subscription above then does the restoring.
    const restore = () => {
      if (!useDragStore.getState().isGestureInFlight) return;
      useDragStore.getState().cancelDrag();
    };
    // Guarded: this component is also mounted under the node test environment
    // and during server rendering, where neither API exists.
    const canListen = typeof globalThis.addEventListener === "function";
    if (canListen) globalThis.addEventListener("blur", restore);
    const doc = typeof document === "undefined" ? null : document;
    const onVisibility = () => {
      if (doc?.visibilityState === "hidden") restore();
    };
    doc?.addEventListener("visibilitychange", onVisibility);

    return () => {
      unsubscribe();
      if (canListen) globalThis.removeEventListener("blur", restore);
      doc?.removeEventListener("visibilitychange", onVisibility);
      // Unmounting mid-drag must not leave the scene permanently deaf to
      // pointer events, nor leave it marked as dragging.
      suppress(false);
      setSceneDragActive(scene, false);
    };
  }, [get, scene]);

  return null;
}
